import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountFanslyDeps } from '../fansly/FanslyDeps'
import { RequestPayoutDialog } from '../fansly/wallet/PayoutDialogs'
import { formatMills } from '../fansly/statistics/statsData'
import { accountCrmRequest } from '../of/accountCrmRequest'
import { submitWithdrawal, type WithdrawOutcome } from '../of/statements/statementsData'
import { WithdrawModal } from '../of/statements/WithdrawModal'
import { usableMethods, type RowAccount, type RowData } from './payoutsData'
import '../library/library.css'
import '../of/ofPages.css'
import '../of/statements/statements.css'
import '../fansly/ui/fansly.css'
import '../fansly/wallet/wallet.css'

export interface RequestDialogProps {
  account: RowAccount
  data: RowData
  onClose: () => void
  /** The platform took the request (or its answer was lost): the page says `message` and reloads the row. */
  onFinished: (message: string) => void
}

/**
 * Request payout from a Payouts row: exactly the dialog that Account's own page
 * opens — OnlyFans' Manual payouts or Fansly's Request Payout — inside the
 * classes those dialogs are styled under.
 */
export function RequestDialog({ account, data, onClose, onFinished }: RequestDialogProps) {
  const { t: tOf } = useTranslation('ofStatements')
  const { t: tFs, i18n } = useTranslation('fanslyWallet')
  const request = useMemo(() => accountCrmRequest(account.account_id), [account.account_id])
  const submit = useCallback((amount: number, key: string) => submitWithdrawal(request, account.account_id, amount, key), [request, account.account_id])
  const d = data.detail

  const finishedOf = (outcome: WithdrawOutcome) => {
    if (outcome.kind !== 'ok') return onFinished(tOf('withdraw.unknown'))
    onFinished(outcome.state === 'rejected' ? tOf('payout.returned') : outcome.state === 'approved' ? tOf('payout.processed') : tOf('payout.processing'))
  }

  return (
    <div className="lib ofp ofx po-dialog-host">
      {d.platform === 'onlyfans' ? (
        <WithdrawModal balances={d.balances} submit={submit} onFinished={finishedOf} onClose={onClose} />
      ) : (
        <AccountFanslyDeps accountId={account.account_id}>
          <RequestPayoutDialog
            methods={usableMethods(d.methods)}
            available={d.availableMills}
            onClose={onClose}
            onRequested={(amount) => onFinished(tFs('request.done', { amount: formatMills(amount, i18n.language) }))}
          />
        </AccountFanslyDeps>
      )}
    </div>
  )
}
